import { useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { appendLocaleToSearchParams } from "~/translations/i18n";
import { useStripeSubscription } from "~/api/billing/queries";
import { ISubscriptionStripe } from "~/api/billing/types";
import handleErrors from "~/helpers/handleErrors";
import { IPaymentValues } from "~/types/billing";
import { useShopifyPayment } from "./useShopifyPayment";
import { toast } from "~/components/toast";
import { useUTM } from "~/hooks/useUTM";
import { UAParser } from 'ua-parser-js';
import { getCheckoutPaymentErrorMessage } from "./paymentErrorMessage";

type Props = {
  planId: string;
  isUserShopify: boolean;
  upgradeWithStore: boolean;
};

export const usePayment = ({
  planId,
  isUserShopify,
  upgradeWithStore = false,
}: Props) => {
  const { mutateAsync: stripeSubscription } = useStripeSubscription();
  const { t } = useTranslation();
  const stripe = useStripe();
  const payShopify = useShopifyPayment(planId);

  const utms = useUTM();
  let parser = new UAParser();

  const payStripe = async (values?: IPaymentValues) => {
    const clientId = await getGaClientId();

    const result = await parser.getResult().withClientHints();

    const payload: ISubscriptionStripe = { plan_id: planId };
    if (values?.name) payload.name = values?.name;
    if (values?.payment_method_id)
      payload.payment_method_id = values?.payment_method_id;
    if (values?.setup_intent) payload.setup_intent = values?.setup_intent;
    if (values?.promo_code_id) payload.promo_code_id = values?.promo_code_id;
    if (upgradeWithStore) payload.upgrade_with_store = true;
    
    try {
      const hasAttribution =
        !!utms.campaign ||
        !!utms.source ||
        !!utms.medium ||
        !!utms.term ||
        !!utms.gclid ||
        !!utms.gbraid ||
        !!utms.wbraid;

      const utmParams: Record<string, unknown> = hasAttribution ? utms : {};
      
      const response = await stripeSubscription({
        ...payload,
        ga4_client_id: clientId ?? '',
        utms: {
          ...utmParams,
          device: result.device.model || 'desktop',
          os: result.os.name + ' ' + result.os.version,
          browser: result.browser.name + ' ' + result.browser.version,
        },
      });
      
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("plan_id", planId);
      searchParams.delete("annual");
      searchParams.delete("annual-plans-modal");
      appendLocaleToSearchParams(searchParams);
      let returnURL = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
      if (response?.client_secret && stripe) {
        const confirmPaymentResponse = await stripe.confirmPayment({
          clientSecret: response.client_secret,
          confirmParams: {
            return_url: returnURL,
          },
        });

        if (confirmPaymentResponse.error) {
          toast.warning(
            getCheckoutPaymentErrorMessage(confirmPaymentResponse.error, t)
          );
          return confirmPaymentResponse?.error;
        }

        return null;
      }
    } catch (err: any) {
      const message = getCheckoutPaymentErrorMessage(
        err?.response?.data?.message,
        t
      );
      toast.warning(message);
      return "error";
    }

    return null;
  };

  const pay = async (values?: IPaymentValues) => {
    if (isUserShopify) {
      return payShopify();
    }

    const toastMessages = {
      loading: t("checkout.loading"),
      success: t("checkout.success"),
      error: t("checkout.error"),
    };
    const { response } = await handleErrors(
      () => payStripe(values),
      toastMessages
    );
    return response;
  };

  return { pay, payStripe, payShopify };
};

const getGaClientId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !(window as any).gtag) {
      resolve(null);
      return;
    }

    (window as any).gtag(
      "get",
      "G-F4VQ0P300P",
      "client_id",
      (clientId: string) => {
        resolve(clientId);
      }
    );
  });
};